/**
 * Hook for fetching OData schema from imported service definitions.
 *
 * This hook provides dynamic navigation properties, fields, and $expand options
 * based on imported *.odata-svc.json files from Infor API Gateway.
 */

import { useQuery } from '@tanstack/react-query';
import { odataServicesApi, ODataSchemaResponse, ODataNavigationProperty } from '../api/client';

interface UseODataSchemaOptions {
  /** Whether to enable the query (default: true when connectionId and endpoint are provided) */
  enabled?: boolean;
  /** Cache time in milliseconds (default: 24 hours) */
  staleTime?: number;
}

interface UseODataSchemaResult {
  /** The schema data from imported service definitions */
  schema: ODataSchemaResponse | null;
  /** Whether the schema is currently loading */
  isLoading: boolean;
  /** Error message if schema fetch failed */
  error: string | null;
  /** Whether a service definition was found for this endpoint */
  hasServiceDefinition: boolean;
  /** Navigation properties formatted for the widget editor */
  navigationProperties: Array<{
    name: string;
    targetEntity: string;
    multiplicity: '1' | '*';
  }>;
  /** Available $expand options */
  expandOptions: string[];
  /** Refetch the schema */
  refetch: () => void;
}

/**
 * Fetches OData schema for a specific endpoint from imported service definitions.
 *
 * @param connectionId - The connection UUID
 * @param endpoint - The endpoint path or entity set name (e.g., "Orders" or "/TENANT/LN/lnapi/odata/.../Orders")
 * @param options - Optional configuration
 *
 * @example
 * ```tsx
 * const { schema, navigationProperties, isLoading, hasServiceDefinition } = useODataSchema(
 *   widget.connection_id,
 *   selectedEndpoint
 * );
 *
 * // Use navigationProperties instead of hardcoded KNOWN_ENTITY_RELATIONS
 * const availableRelations = hasServiceDefinition
 *   ? navigationProperties
 *   : KNOWN_ENTITY_RELATIONS[endpoint] || [];
 * ```
 */
export function useODataSchema(
  connectionId: string | null | undefined,
  endpoint: string | null | undefined,
  options: UseODataSchemaOptions = {}
): UseODataSchemaResult {
  const {
    enabled = true,
    staleTime = 24 * 60 * 60 * 1000, // 24 hours - schemas rarely change
  } = options;

  const queryEnabled = enabled && !!connectionId && !!endpoint;

  const {
    data: schema,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['odata-schema', connectionId, endpoint],
    queryFn: () => odataServicesApi.getSchema(connectionId!, endpoint!),
    enabled: queryEnabled,
    staleTime,
    // Don't throw on error - we want to fall back to hardcoded values
    retry: 1,
  });

  // Format navigation properties for the widget editor
  const navigationProperties = (schema?.navigation_properties || []).map(
    (np: ODataNavigationProperty) => ({
      name: np.name,
      targetEntity: np.target_entity,
      multiplicity: np.multiplicity,
    })
  );

  const expandOptions = schema?.expand_options || [];

  // Check if we actually found a service definition
  const hasServiceDefinition = !!schema?.service_namespace;

  return {
    schema: schema || null,
    isLoading: queryEnabled ? isLoading : false,
    error: error ? (error as Error).message : null,
    hasServiceDefinition,
    navigationProperties,
    expandOptions,
    refetch,
  };
}

/**
 * Hook to list all service definitions for a connection.
 */
export function useODataServiceDefinitions(connectionId?: string) {
  return useQuery({
    queryKey: ['odata-services', connectionId],
    queryFn: () => odataServicesApi.list(connectionId),
    enabled: true,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

export default useODataSchema;
